import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getList } from "@/lib/api";
import { PageHeader } from "@/components/Editorial";

const PAGES = [
  { title: "À propos — Qui est Dorothée Lancelot ?", to: "/a-propos", tags: "biographie auteure médium parcours", kind: "Page" },
  { title: "Histoire & Ésotérisme", to: "/histoire-esoterisme", tags: "occultisme mysticisme cartomancie astrologie spiritisme magnétisme divination histoire", kind: "Sujet" },
  { title: "Médiumnité", to: "/mediumnite", tags: "voyance contact défunt intuition croyance", kind: "Sujet" },
  { title: "Onirothérapie", to: "/onirotherapie", tags: "rêves symboles inconscient jung", kind: "Sujet" },
  { title: "Hypnose Ericksonienne", to: "/hypnose", tags: "hypnose accompagnement", kind: "Sujet" },
  { title: "Deuil", to: "/deuil", tags: "deuil absence signes souvenirs aaesad", kind: "Sujet" },
  { title: "Conférences & interventions", to: "/conferences", tags: "conférence salon rencontre thèmes", kind: "Page" },
  { title: "Les Chroniques de l'Étrange", to: "/les-chroniques-de-letrange", tags: "légendes histoire mysticisme occultisme chroniques", kind: "Projet" },
  { title: "Consultations", to: "/consultations", tags: "voyance numérologie lecture d'âme tarifs", kind: "Page" },
  { title: "Presse & Médias", to: "/presse-medias", tags: "presse interview radio média", kind: "Page" },
  { title: "AAESAD — Aide et Soutien au Deuil", to: "/aaesad", tags: "association deuil soutien", kind: "Page" },
  { title: "Demandez à Dorothée IA", to: "/dorothee-ia", tags: "ia carte tarot guidance question", kind: "Expérience" },
  { title: "L'Univers", to: "/univers", tags: "univers écriture histoire ésotérisme", kind: "Page" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const { data: books = [] } = useQuery({ queryKey: ["books"], queryFn: () => getList("books") });

  useSeo({ title: "Recherche", description: "Recherchez parmi les livres, les chroniques, les conférences et les sujets ésotériques du site de Dorothée Lancelot.", path: "/recherche" });

  const query = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!query) return [];
    const bookHits = books
      .filter((b) => `${b.title} ${b.genre} ${(b.themes || []).join(" ")} ${b.summary_short}`.toLowerCase().includes(query))
      .map((b) => ({ title: b.title, to: `/livres/${b.slug}`, kind: "Livre" }));
    const pageHits = PAGES.filter((p) => `${p.title} ${p.tags}`.toLowerCase().includes(query));
    return [...bookHits, ...pageHits];
  }, [query, books]);

  return (
    <div>
      <PageHeader eyebrow="Explorer" title="Recherche" intro="Livres, chroniques, conférences et sujets ésotériques — trouvez tout ce que vous cherchez." />
      <section className="dl-container py-14 max-w-3xl">
        <div className="flex items-center gap-3 border border-[rgba(198,167,106,0.35)] bg-[#0B1020] px-5 py-4 focus-within:border-[#C6A76A]">
          <SearchIcon size={20} className="text-[#C6A76A]" />
          <input
            data-testid="search-input"
            autoFocus
            value={q}
            onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}); }}
            placeholder="Rechercher un livre, un thème, une chronique…"
            className="flex-1 bg-transparent text-[#F3E9D2] placeholder:text-[#F3E9D2]/35 focus:outline-none"
          />
        </div>

        <div className="mt-8 space-y-2" data-testid="search-results">
          {query && results.length === 0 && <p className="text-[#F3E9D2]/50 italic">Aucun résultat pour « {q} ».</p>}
          {results.map((r, i) => (
            <Link key={i} to={r.to} className="dl-card flex items-center justify-between px-5 py-4 group">
              <span className="serif text-lg text-[#F3E9D2] group-hover:text-[#D4B978]">{r.title}</span>
              <span className="dl-eyebrow" style={{ fontSize: "0.55rem" }}>{r.kind}</span>
            </Link>
          ))}
          {!query && <p className="text-[#F3E9D2]/45 italic">Commencez à taper pour rechercher.</p>}
        </div>
      </section>
    </div>
  );
}
